import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xhrRequest } from '../lib/xhrRequest';
import {
  ArrowLeft, Trophy, TrendingUp, Loader2, History, Home, List, User,
  CheckCircle, AlertTriangle, Clock,
} from 'lucide-react';
import Footer from '../components/Footer';

const formatCents = (c) =>
  `$${(Math.max(0, c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (key) => {
  const [y, m] = key.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const statusBadge = (status) => {
  const styles = {
    closed:        { icon: Clock,        color: 'text-amber-300  bg-amber-500/10  border-amber-500/30',  label: 'Closed, awaiting payout' },
    paid:          { icon: CheckCircle,  color: 'text-green-300  bg-green-500/10  border-green-500/30',  label: 'Paid' },
    partially_paid:{ icon: AlertTriangle,color: 'text-amber-300  bg-amber-500/10  border-amber-500/30',  label: 'Partially paid' },
    payout_failed: { icon: AlertTriangle,color: 'text-red-300    bg-red-500/10    border-red-500/30',    label: 'Payout failed' },
  }[status] || { icon: Clock, color: 'text-white bg-white/5 border-white/10', label: status };
  const Icon = styles.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${styles.color}`}
          data-testid={`period-status-${status}`}>
      <Icon size={12} /> {styles.label}
    </span>
  );
};

function WinnerRow({ entry, paid }) {
  const medal = ['🥇', '🥈', '🥉'][entry.rank - 1] || '';
  const wasPaid = paid?.status === 'paid';
  const wasSkipped = paid?.status === 'skipped';
  return (
    <div className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
      <span className="w-6 text-center">{medal}</span>
      <div className="w-8 h-8 rounded-lg bg-white/10 overflow-hidden flex items-center justify-center">
        {entry.photoUrl
          ? <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
          : <span className="font-bold text-sm">{entry.displayName?.[0]?.toUpperCase()}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate text-sm">{entry.displayName}</p>
        <p className="text-xs text-[#A1A1AA] truncate">{entry.serviceTitle}</p>
      </div>
      <div className="text-right">
        <p className="font-mono text-sm text-[#F5C542]">{formatCents(entry.payoutCents)}</p>
        {paid && (
          <p className={`text-[10px] uppercase tracking-wider ${wasPaid ? 'text-green-400' : wasSkipped ? 'text-amber-400' : 'text-red-400'}`}>
            {wasPaid ? 'Paid' : wasSkipped ? `Skipped · ${paid.reason || ''}` : (paid.status || 'unknown')}
          </p>
        )}
      </div>
    </div>
  );
}

export default function ChallengeHistoryPage() {
  const { token } = useAuth();
  const [periods, setPeriods] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrRequest('GET',
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/history`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (cancelled) return;
      if (!ok) return setError(data.detail || 'Failed to load history');
      setPeriods(data.periods || []);
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="challenge-history-page">
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <Link to="/dashboard/leaderboard" className="text-[#A1A1AA] hover:text-white" data-testid="history-back-link">
            <ArrowLeft size={20} />
          </Link>
          <History size={20} className="text-[#F5C542]" />
          <h1 className="font-heading font-bold text-lg">Challenge history</h1>
        </div>
      </header>

      <main className="p-4 md:p-8 pb-24 md:pb-8 max-w-3xl mx-auto space-y-6">
        {error && (
          <div className="stat-card text-red-300" data-testid="history-error">{error}</div>
        )}

        {periods === null && !error && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-[#F5C542]" size={28} />
          </div>
        )}

        {periods && periods.length === 0 && (
          <div className="stat-card text-center py-10" data-testid="history-empty">
            <History size={28} className="text-[#A1A1AA] mx-auto mb-2" />
            <p className="font-semibold">No past challenges yet.</p>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Winners will appear here once a monthly challenge closes.
            </p>
          </div>
        )}

        {periods && periods.map((p) => {
          const paidByUser = {};
          (p.payoutRecords || []).forEach((r) => { paidByUser[r.userId] = r; });
          return (
            <section key={p.periodKey} className="stat-card" data-testid={`history-period-${p.periodKey}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-heading font-bold text-xl">{monthLabel(p.periodKey)}</h3>
                  <p className="text-xs text-[#A1A1AA] mt-0.5">
                    Pool: <span className="text-[#F5C542] font-mono">{formatCents(p.frozenPoolCents ?? p.poolCents)}</span>
                    {typeof p.totalPaidCents === 'number' && (
                      <>{' '}· Paid out: <span className="text-white font-mono">{formatCents(p.totalPaidCents)}</span></>
                    )}
                  </p>
                </div>
                {statusBadge(p.status)}
              </div>

              {(p.frozenTopEarner?.length > 0) && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2 text-sm text-[#A1A1AA]">
                    <Trophy size={14} className="text-[#F5C542]" /> Top Earner
                  </div>
                  {p.frozenTopEarner.map((e) => (
                    <WinnerRow key={`t-${e.userId}`} entry={e} paid={paidByUser[e.userId]} />
                  ))}
                </div>
              )}

              {(p.frozenHighestRate?.length > 0) && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2 text-sm text-[#A1A1AA]">
                    <TrendingUp size={14} /> Highest Tip Rate
                  </div>
                  {p.frozenHighestRate.map((e) => (
                    <WinnerRow key={`r-${e.userId}`} entry={e} paid={paidByUser[e.userId]} />
                  ))}
                </div>
              )}

              {(!p.frozenTopEarner?.length && !p.frozenHighestRate?.length) && (
                <p className="text-sm text-[#A1A1AA] italic">No qualifying workers this period.</p>
              )}
            </section>
          );
        })}

        <Footer className="mt-6" />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 px-4 py-2 flex items-center justify-around z-50">
        <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <Home size={20} /><span className="text-xs">Home</span>
        </Link>
        <Link to="/dashboard/tips" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <List size={20} /><span className="text-xs">Tips</span>
        </Link>
        <Link to="/dashboard/leaderboard" className="flex flex-col items-center gap-1 py-2 px-3 text-[#F5C542]">
          <Trophy size={20} /><span className="text-xs">Challenge</span>
        </Link>
        <Link to="/dashboard/settings" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <User size={20} /><span className="text-xs">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
